// src/ExamQuestions9.jsx
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from '../css/modules/ExamQuestions.module.css';

const ExamQuestions9 = () => {
  const navigate = useNavigate();

  const handleNext = () => {
    navigate('/');
  };

  return (
    <div className={styles.contactContainer}>
      <Helmet>
        <title>Przygotuj się na patent strzelecki – poznaj pytania egzaminacyjne na broń</title>
        <meta
          name="description"
          content="Ostatnia część pytań egzaminacyjnych na patent strzelecki. Sprawdź, jakie są konsekwencje prawne związane z bronią, granice obrony koniecznej i stanu wyższej konieczności."
        />
      </Helmet>

      <div className={styles.content}>
        <h1 className="mainHeader">Patent strzelecki i na broń – nauka na bazie sprawdzonych pytań</h1>

        <div className="intro">
          To już ostatnia część zestawu pytań, który przygotowuje Cię do <strong>egzaminu na broń</strong> i uzyskania <strong>patentu strzeleckiego</strong>. Tym razem skupiamy się na zagadnieniach z zakresu odpowiedzialności karnej, obrony koniecznej oraz stanu wyższej konieczności – niezwykle ważnych dla każdego posiadacza <strong>pozwolenia na broń</strong>.

          📘 Pytania zostały opracowane na podstawie ustawy o broni i amunicji oraz przepisów Kodeksu karnego. Jeśli chcesz zdać egzamin nie tylko formalnie, ale także świadomie – przeczytaj je dokładnie.

          🔗 Sprawdź też:
          <ul>
            <li><a href="/">Jak wygląda egzamin na broń?</a></li>
            <li><a href="/pytania-na-pozwolenie-na-bron-patent-strzelecki">Co trzeba wiedzieć o pozwoleniu na broń?</a></li>
            <li><a href="https://pistolet.org" target="_blank" rel="noopener noreferrer">Testy online na pistolet.org</a></li>
          </ul>
        </div>

        <h2>⚖️ Przestępstwa i wykroczenia związane z bronią</h2>

        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>131. 📩 Co grozi za brak zgłoszenia zbycia lub utraty broni/amunicji?</h3>
            <p>
              Zgodnie z ustawą o broni i amunicji, każda utrata, zbycie lub kradzież broni musi być niezwłocznie zgłoszona do organów Policji. Brak takiego zgłoszenia to wykroczenie zagrożone karą aresztu lub grzywny – nawet jeśli zdarzenie nie było zawinione.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>132. 🏠 Co grozi za brak zgłoszenia zmiany miejsca stałego pobytu?</h3>
            <p>
              Osoba posiadająca broń lub pozwolenie na broń ma obowiązek poinformować właściwy organ w terminie 14 dni o zmianie miejsca zamieszkania. Niezgłoszenie zmiany adresu może skutkować karą grzywny lub aresztem, a także utratą pozwolenia.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>133. 🧯 Co grozi za nieumyślną utratę broni lub amunicji?</h3>
            <p>
              Nawet nieumyślne zgubienie broni (np. w transporcie) może skutkować karą grzywny, ograniczenia wolności albo pozbawienia wolności do roku. Odpowiedzialność karna wynika z samego faktu naruszenia zasad bezpieczeństwa.
            </p>
          </li>
        </ul>

        <h2>🛡️ Obrona konieczna i stan wyższej konieczności</h2>

        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>134. ⚖️ Czym jest stan wyższej konieczności?</h3>
            <p>
              Stan wyższej konieczności występuje, gdy działamy w celu uchylenia bezpośredniego niebezpieczeństwa grożącego dobru chronionemu prawem, poświęcając inne dobro o mniejszej wartości. Przykład: złamanie przepisów w celu ratowania życia.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>135. 🛡️ Czym jest obrona konieczna?</h3>
            <p>
              To działanie podejmowane w celu odparcia bezpośredniego, bezprawnego zamachu na życie, zdrowie, mienie lub inne dobro. Prawo zezwala na obronę – nawet przy użyciu broni – jeśli atak nastąpił nagle i zagrażał istotnym wartościom.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>136. 🙏 Czy sąd może odstąpić od kary za przekroczenie obrony koniecznej?</h3>
            <p>
              Tak. Jeśli przekroczenie granic obrony nastąpiło wskutek silnego wzburzenia lub strachu, który był uzasadniony sytuacją (np. nocny atak w mieszkaniu), sąd może całkowicie odstąpić od wymierzenia kary.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>137. 🧠 Czy sąd może złagodzić karę za przekroczenie stanu wyższej konieczności?</h3>
            <p>
              Może. Zwłaszcza jeśli okoliczności wskazują, że działanie podjęte było w dobrej wierze i miało na celu ratowanie życia lub zdrowia, nawet jeśli proporcje dobra poświęcanego i ratowanego były niewłaściwe.
            </p>
          </li>
        </ul>

        <h2>🚫 Naruszenia przepisów i ich konsekwencje</h2>

        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>138. 🔒 Co grozi za noszenie broni z naruszeniem ograniczenia lub zakazu?</h3>
            <p>
              Posiadacz broni, który łamie zakaz noszenia jej przy sobie (np. w przestrzeni publicznej, mimo warunkowego pozwolenia), może podlegać karze grzywny lub aresztu. Może to też skutkować cofnięciem pozwolenia.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>139. 🎯 Co grozi za użycie broni poza strzelnicą w celach szkoleniowych/sportowych?</h3>
            <p>
              Zgodnie z przepisami, broń do celów sportowych może być używana wyłącznie na strzelnicach. Jej użycie poza nimi, nawet przez osobę z pozwoleniem, jest naruszeniem i może skutkować sankcją karną.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>140. 🛂 Co grozi za przywóz broni spoza UE bez dokumentu konsula?</h3>
            <p>
              Każda broń przywożona spoza Unii Europejskiej musi być legalizowana dokumentem wydanym przez konsula RP. Brak tego dokumentu oznacza naruszenie przepisów celnych i może skutkować aresztem lub grzywną.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>141. 🧨 Co grozi za produkcję lub handel bronią bez zezwolenia?</h3>
            <p>
              Nielegalna produkcja, przetwarzanie lub handel bronią palną to przestępstwo zagrożone karą od 1 do 10 lat pozbawienia wolności. Dotyczy to zarówno działalności komercyjnej, jak i produkcji w celach osobistych.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>142. 🔫 Co grozi za nielegalne posiadanie broni palnej lub amunicji?</h3>
            <p>
              Zgodnie z Kodeksem karnym, posiadanie broni palnej lub amunicji bez wymaganego pozwolenia jest przestępstwem. Grozi za nie kara pozbawienia wolności od 6 miesięcy do 8 lat.
            </p>
          </li>
        </ul>

        <div className="buttonContainer">
          <button onClick={handleNext} className={styles.primaryButton}>
            Sprawdź testy na patent strzelecki
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExamQuestions9;
